import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { useSession } from "@/lib/auth";
import { isOpsPortal } from "@/lib/portal";

/**
 * Shared back-office route gate. Shows "Checking your access…" while the
 * session probe is in flight, bounces unauthenticated users to /login, admits sessions whose
 * `platform_role` satisfies `allow`, and host-aware-redirects everyone else
 * (ops.* has no /admin route, the apex has no operations /login dead-end). The
 * API independently enforces the boundary (403), so this is purely a UX guard.
 */
function RoleGate({
  allow,
  reject,
}: {
  allow: (platformRole: string) => boolean;
  reject: { ops: string; apex: string };
}) {
  const { pathname } = useLocation();
  const { data: session, isLoading } = useSession();

  if (isLoading) {
    // B7 (2026-09-13 browser QA): this used to be an empty div, so a slow
    // `/api/auth/me` (seen once at >2.5s) read as a broken white page rather
    // than as loading. Say so instead.
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background"
        aria-busy="true"
        role="status"
      >
        <span className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Checking your access…
        </span>
      </div>
    );
  }
  if (!session) {
    return <Navigate to="/login" replace state={{ from: pathname }} />;
  }
  if (!allow(session.platform_role)) {
    return <Navigate to={isOpsPortal ? reject.ops : reject.apex} replace />;
  }
  return <Outlet />;
}

/**
 * Gate for the FULL creator-only Back office (analytics, PII tabs, operator
 * management). Requires `platform_role === "superuser"` — or the read-only
 * `viewer` (OPS-29), which reads every one of these tabs and can change nothing
 * (the API refuses its writes; the pages render no action control for it).
 *
 * Reject target is host-aware: on the apex, non-superusers go to /admin; on the
 * operations portal (ops.*) there is no /admin route, so a support operator who
 * hits a superuser-only tab is sent back to /backoffice (its index dispatches
 * them to Recovery) — never to /admin, which would dead-end in a redirect loop.
 */
export function RequireSuper() {
  return (
    <RoleGate
      allow={(role) => role === "superuser" || role === "viewer"}
      reject={{ ops: "/backoffice", apex: "/admin" }}
    />
  );
}

/**
 * Gate for the Back office shell. Admits `support`, `superuser` and the
 * read-only `viewer` (OPS-29). Individual superuser-only tabs still redirect
 * support away client-side and 403 server-side.
 *
 * Reject target is host-aware: on the apex a non-operator goes to /admin; on the
 * operations portal (ops.*) there is no recruiter dashboard, so they go to
 * /login — where the page detects the operator-less session and shows a clear
 * "no operations access" message (see Login.tsx) instead of looping.
 */
export function RequirePlatformOperator() {
  return (
    <RoleGate
      allow={(role) =>
        role === "support" || role === "superuser" || role === "viewer"
      }
      reject={{ ops: "/login", apex: "/admin" }}
    />
  );
}
